import React, { useState, useEffect } from 'react';
import { translations } from './Translation'; // Make sure this path is correct

function HowItWorks({ role = 'client', onNavigate, darkMode }) {
  const [activeRole, setActiveRole] = useState(role);
  
  // Translation Engine
  const [language, setLanguage] = useState(localStorage.getItem('elitecut_lang') || 'en');
  const t = translations[language] || translations.en;

  useEffect(() => {
    const handleStorage = () => setLanguage(localStorage.getItem('elitecut_lang') || 'en');
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // Dynamic content mapped to the translation files
  const data = {
    client: {
      title: t.forClients,
      color: "#ec4899", // Pink
      steps: [
        { step: '01', title: t.clientStep1Title, desc: t.clientStep1Desc, icon: '🔍' },
        { step: '02', title: t.clientStep2Title, desc: t.clientStep2Desc, icon: '📅' },
        { step: '03', title: t.clientStep3Title, desc: t.clientStep3Desc, icon: '✨' }
      ]
    },
    staff: {
      title: t.forProfessionals,
      color: "#f97316", // Orange
      steps: [
        { step: '01', title: t.staffStep1Title, desc: t.staffStep1Desc, icon: '📸' },
        { step: '02', title: t.staffStep2Title, desc: t.staffStep2Desc, icon: '✂️' },
        { step: '03', title: t.staffStep3Title, desc: t.staffStep3Desc, icon: '🤝' }
      ]
    },
    partner: {
      title: t.forOwners,
      color: "#3b82f6", // Blue
      steps: [
        { step: '01', title: t.ownerStep1Title, desc: t.ownerStep1Desc, icon: '🏢' },
        { step: '02', title: t.ownerStep2Title, desc: t.ownerStep2Desc, icon: '✨' },
        { step: '03', title: t.ownerStep3Title, desc: t.ownerStep3Desc, icon: '✂️' },
        { step: '04', title: t.ownerStep4Title, desc: t.ownerStep4Desc, icon: '📅' },
        { step: '05', title: t.ownerStep5Title, desc: t.ownerStep5Desc, icon: '🚀' }
      ]
    }
  };

  const currentData = data[activeRole] || data.client;

  return (
    <div style={{ background: darkMode ? '#0f172a' : '#f8fafc', minHeight: '100vh', color: darkMode ? 'white' : '#0f172a', paddingBottom: '80px', transition: 'background-color 0.3s' }}>
      
      {/* Top Navigation */}
      <nav style={{ background: darkMode ? '#1e293b' : 'white', padding: '15px 40px', borderBottom: darkMode ? '1px solid #334155' : '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 100 }}>
        <button onClick={() => onNavigate('home')} className="premium-login-btn" style={{ background: darkMode ? '#334155' : '#f1f5f9', color: darkMode ? 'white' : '#0f172a', border: 'none', boxShadow: 'none' }}>
          {t.backToHome}
        </button>

        {/* Role Toggle Menu */}
        <div style={{ display: 'flex', background: darkMode ? '#0f172a' : '#f1f5f9', padding: '5px', borderRadius: '30px', border: darkMode ? '1px solid #334155' : '1px solid #e2e8f0' }}>
          <button 
            onClick={() => setActiveRole('client')}
            style={{ background: activeRole === 'client' ? data.client.color : 'transparent', color: activeRole === 'client' ? 'white' : '#64748b', boxShadow: activeRole === 'client' ? '0 2px 10px rgba(0,0,0,0.1)' : 'none', border: 'none', padding: '8px 20px', borderRadius: '25px', fontWeight: 'bold', cursor: 'pointer', transition: 'all 0.3s ease' }}
          >
            {t.tabClients}
          </button>
          <button 
            onClick={() => setActiveRole('staff')}
            style={{ background: activeRole === 'staff' ? data.staff.color : 'transparent', color: activeRole === 'staff' ? 'white' : '#64748b', boxShadow: activeRole === 'staff' ? '0 2px 10px rgba(0,0,0,0.1)' : 'none', border: 'none', padding: '8px 20px', borderRadius: '25px', fontWeight: 'bold', cursor: 'pointer', transition: 'all 0.3s ease' }}
          >
            {t.tabStaff}
          </button>
          <button 
            onClick={() => setActiveRole('partner')}
            style={{ background: activeRole === 'partner' ? data.partner.color : 'transparent', color: activeRole === 'partner' ? 'white' : '#64748b', boxShadow: activeRole === 'partner' ? '0 2px 10px rgba(0,0,0,0.1)' : 'none', border: 'none', padding: '8px 20px', borderRadius: '25px', fontWeight: 'bold', cursor: 'pointer', transition: 'all 0.3s ease' }}
          >
            {t.tabOwners}
          </button>
        </div>
      </nav>

      {/* Header */}
      <div style={{ textAlign: 'center', paddingTop: '60px', paddingBottom: '40px' }}>
        <h1 style={{ fontSize: '42px', fontFamily: 'serif', margin: '0 0 15px 0', textTransform: 'uppercase', letterSpacing: '2px', color: currentData.color, transition: 'color 0.3s' }}>
          {t.howItWorksTitle}
        </h1>
        <p style={{ fontSize: '18px', color: darkMode ? '#94a3b8' : '#64748b', fontWeight: 'bold', letterSpacing: '4px', textTransform: 'uppercase' }}>
          {currentData.title}
        </p>
        <div style={{ width: '60px', height: '4px', background: currentData.color, margin: '20px auto 0 auto', transition: 'background-color 0.3s' }}></div>
      </div>

      {/* Vertical Timeline Container */}
      <div className="timeline-container">
        {currentData.steps.map((step, index) => {
          const isEven = index % 2 === 0; 
          return (
            <div className={`timeline-row ${isEven ? 'row-left' : 'row-right'}`} key={index}>
              
              {/* Text Side */}
              <div className="timeline-text">
                <h3 style={{ color: currentData.color, fontSize: '24px', margin: '0 0 15px 0', transition: 'color 0.3s' }}>{step.title}</h3>
                <p style={{ color: darkMode ? '#cbd5e1' : '#475569', fontSize: '15px', lineHeight: '1.8' }}>{step.desc}</p>
              </div>

              {/* Center Dotted Line & Number */}
              <div className="timeline-center">
                <div className="step-label">{t.stepText}</div>
                <div className="step-number" style={{ color: darkMode ? 'white' : '#0f172a' }}>{step.step}</div>
              </div>

              {/* Icon Side */}
              <div className="timeline-visual">
                <div className="visual-circle" style={{ background: currentData.color, transition: 'background-color 0.3s' }}>
                  <span style={{ fontSize: '60px' }}>{step.icon}</span>
                </div>
              </div>

            </div>
          );
        })}
      </div>
    </div>
  );
}

export default HowItWorks;